import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import Button from "../../buttons";
import TextEditor from "../../TextEditor";
import { ReshareApiService } from "../../../../api/urls";
import Lottie from "lottie-react";
import DotLoader from "../../../../assets/Animation/Dot Loader.json";
import { ReactComponent as AlertIcon } from "../../../../assets/icons/svg/Alert-icon.svg";
import { DEFAULT_FONT_FAMILY } from "../../../../config/fontConfig";

export interface ScreenRecordingPreview {
  dataUrl: string;
  blob: Blob;
  duration: number;
  timestamp: number;
}

export interface IframeDimensions {
  left: number;
  top: number;
  width: number;
  height: number;
  viewportWidth?: number;
  viewportHeight?: number;
  outerWidth?: number;
  outerHeight?: number;
  chromeTop?: number;
  chromeLeft?: number;
  screenX?: number;
  screenY?: number;
  screenWidth?: number;
  screenHeight?: number;
}

export interface RecordingSubmission {
  id: string;
  recordingBlob?: Blob;
  dataUrl: string;
  feedback: string;
  hasAudio: boolean;
  duration: number;
  timestamp: number;
  uniqueFeedbackId?: string;
  thumbnailUrl?: string;
  startRect?: DOMRect | null;
  annotationImage?: string;
}

interface ScreenRecordingPreviewProps {
  preview: ScreenRecordingPreview;
  onClose: () => void;
  onDownload: () => void;
  onSubmit?: (submission: RecordingSubmission) => void;
  uniqueFeedbackId?: string;
  isEditMode?: boolean;
  existingFeedback?: string;
  existingRecordingBlob?: Blob;
  existingDataUrl?: string;
  existingThumbnailUrl?: string;
  annotationImage?: string;
  isFromViewListFeedback?: boolean;
  iframeDimensions?: IframeDimensions | null;
}

// Re-encodes a video blob keeping only the specified source rectangle (in video pixels).
// Uses Canvas + MediaRecorder so it works purely in the browser with no server round-trip.
async function cropVideoBlob(
  blob: Blob,
  sx: number, sy: number,  // top-left of crop region in video pixels
  sw: number, sh: number,  // size of crop region in video pixels
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    const blobUrl = URL.createObjectURL(blob);
    video.src = blobUrl;
    video.muted = true;
    video.playsInline = true;

    const canvas = document.createElement("canvas");
    canvas.width = sw;
    canvas.height = sh;
    const ctx = canvas.getContext("2d");
    if (!ctx) { URL.revokeObjectURL(blobUrl); reject(new Error("No 2D context")); return; }

    video.onloadedmetadata = () => {
      const canvasStream = canvas.captureStream(30);

      // Build a combined stream: canvas video + source audio tracks for A/V sync
      const sourceStream: MediaStream | undefined = (video as any).captureStream?.();
      const combinedStream = new MediaStream([
        canvasStream.getVideoTracks()[0],
        ...(sourceStream?.getAudioTracks() ?? []),
      ]);

      // Prefer MP4 (same as the original recording) so the server accepts it
      const mimeType =
        ["video/mp4;codecs=h264,aac", "video/mp4", "video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"]
          .find((t) => MediaRecorder.isTypeSupported(t)) ?? "video/webm";

      const recorder = new MediaRecorder(combinedStream, { mimeType, videoBitsPerSecond: 5_000_000 });
      const chunks: Blob[] = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      recorder.onstop = () => { URL.revokeObjectURL(blobUrl); resolve(new Blob(chunks, { type: mimeType })); };
      recorder.onerror = (e: any) => { URL.revokeObjectURL(blobUrl); reject(e.error || new Error("Recorder error")); };

      let rafId: number;
      const drawFrame = () => {
        ctx.drawImage(video, sx, sy, sw, sh, 0, 0, sw, sh);
        if (!video.ended) rafId = requestAnimationFrame(drawFrame);
      };

      // Start recorder AFTER video.play() so audio and video timestamps are aligned
      video.play().then(() => {
        recorder.start(100);
        rafId = requestAnimationFrame(drawFrame);
      }).catch(reject);
      video.onended = () => { cancelAnimationFrame(rafId); setTimeout(() => recorder.stop(), 200); };
    };

    video.onerror = () => { URL.revokeObjectURL(blobUrl); reject(new Error("Video load failed")); };
  });
}

const ScreenRecordingPreview: React.FC<ScreenRecordingPreviewProps> = ({
  preview,
  onClose,
  onDownload,
  onSubmit,
  uniqueFeedbackId,
  isEditMode,
  existingFeedback,
  existingRecordingBlob,
  existingDataUrl,
  existingThumbnailUrl,
  isFromViewListFeedback = false,
  iframeDimensions,
}) => {
  const [feedback, setFeedback] = useState("");
  const previewContainerRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoNatural, setVideoNatural] = useState<{ w: number; h: number } | null>(null);

  // Re-read live iframe rect at preview time (same as screenshot code approach)
  const [liveIframeRect, setLiveIframeRect] = useState<{ left: number; top: number; width: number; height: number } | null>(null);

  useEffect(() => {
    if (!iframeDimensions) return;
    const iframe = document.querySelector("iframe") as HTMLIFrameElement | null;
    if (iframe) {
      const rect = iframe.getBoundingClientRect();
      setLiveIframeRect({ left: rect.left, top: rect.top, width: rect.width, height: rect.height });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!iframeDimensions]);

  const [animationPhase, setAnimationPhase] = useState<
    "opening" | "open" | "closing" | "closed"
  >("closed");
  const [isAnimating, setIsAnimating] = useState(false);

  const [isRecordingInitialized, setIsRecordingInitialized] = useState(false);

  const [isBlobLoading, setIsBlobLoading] = useState(false);
  const [effectiveDataUrl, setEffectiveDataUrl] = useState<string>("");
  const [blobError, setBlobError] = useState<string>("");
  const [isTextEditorFocused, setIsTextEditorFocused] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string>("");
  const [hasInteractedWithEditor, setHasInteractedWithEditor] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const croppingPromiseRef = useRef<Promise<Blob | null> | null>(null);

  useEffect(() => {
    // Dispatch event when preview opens
    if (preview) {
      console.log(
        "📹 ScreenRecordingPreview: Dispatching preview-opened event",
      );
      window.dispatchEvent(new CustomEvent("preview-opened"));
    }

    // Cleanup: dispatch event when preview closes
    return () => {
      console.log(
        "📹 ScreenRecordingPreview: Dispatching preview-closed event",
      );
      window.dispatchEvent(new CustomEvent("preview-closed"));
    };
  }, [preview]);

  // Check if feedback has actual text content (strip HTML tags)
  const hasActualTextContent = (htmlContent: string): boolean => {
    // Remove HTML tags and check if there's actual text
    const textOnly = htmlContent.replace(/<[^>]*>/g, "").trim();
    return textOnly.length > 0;
  };

  // Show button as enabled when text editor has been interacted with OR has content
  const shouldEnableButton =
    hasInteractedWithEditor ||
    (feedback.trim().length > 0 && hasActualTextContent(feedback));

  // Actually has content for submission validation
  const hasContent =
    feedback.trim().length > 0 && hasActualTextContent(feedback);

  const hasValidVideoSource = effectiveDataUrl && effectiveDataUrl.length > 0;

  // Mirror screenshot's capture-type detection (same logic as ScreenshotManager)
  const isMacOS = /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);
  const outerW = iframeDimensions?.outerWidth ?? window.outerWidth;
  const outerH = iframeDimensions?.outerHeight ?? window.outerHeight;
  const screenW = iframeDimensions?.screenWidth ?? window.screen.width;
  const screenH = iframeDimensions?.screenHeight ?? window.screen.height;
  const scrX = iframeDimensions?.screenX ?? (window.screenX || 0);
  const scrY = iframeDimensions?.screenY ?? (window.screenY || 0);
  const chromeTop = iframeDimensions?.chromeTop ?? (window.outerHeight - window.innerHeight);
  const chromeLeft = iframeDimensions?.chromeLeft ?? Math.max(0, (window.outerWidth - window.innerWidth) / 2);

  const isFullscreen = window.innerHeight === window.screen.height && window.innerWidth === window.screen.width;
  const videoAspect = videoNatural ? videoNatural.w / videoNatural.h : outerW / outerH;
  const screenAspect = screenW / screenH;
  const useMacOSScreenMode = isMacOS && !isFullscreen && Math.abs(videoAspect - screenAspect) < 0.1;

  // Use live rect (read at preview time) when available — same as screenshot code
  const iframeLeft = liveIframeRect?.left ?? iframeDimensions?.left ?? 0;
  const iframeTop = liveIframeRect?.top ?? iframeDimensions?.top ?? 0;
  const iframeW_crop = liveIframeRect?.width ?? iframeDimensions?.width ?? 1;
  const iframeH_crop = liveIframeRect?.height ?? iframeDimensions?.height ?? 1;

  // For macOS screen capture: iframe position in screen coords; otherwise: iframe pos in window coords
  const effectiveIframeLeft = useMacOSScreenMode
    ? scrX + chromeLeft + iframeLeft
    : iframeLeft + chromeLeft;
  const effectiveIframeTop = useMacOSScreenMode
    ? scrY + chromeTop + iframeTop
    : iframeTop + chromeTop;

  // Total captured area: screen for macOS non-fullscreen, outerWindow for all others
  const capturedW = useMacOSScreenMode ? screenW : outerW;
  const capturedH = useMacOSScreenMode ? screenH : outerH;

  if (iframeDimensions) {
    console.log(
      `🎬 CROP capturedW=${capturedW} capturedH=${capturedH}` +
      ` | iframeW=${iframeW_crop.toFixed(0)} iframeH=${iframeH_crop.toFixed(0)}` +
      ` | effLeft=${effectiveIframeLeft.toFixed(0)} effTop=${effectiveIframeTop.toFixed(0)}` +
      ` | outerW=${outerW} outerH=${outerH} chromeTop=${chromeTop}` +
      ` | liveRect=${liveIframeRect ? liveIframeRect.width.toFixed(0)+"x"+liveIframeRect.height.toFixed(0)+"@"+liveIframeRect.left.toFixed(0)+","+liveIframeRect.top.toFixed(0) : "null"}` +
      ` | videoNatural=${videoNatural ? videoNatural.w+"x"+videoNatural.h : "null"}` +
      ` | vidW%=${((capturedW / iframeW_crop) * 100).toFixed(1)}` +
      ` | tx=${(-(effectiveIframeLeft / capturedW) * 100).toFixed(1)}% ty=${(-(effectiveIframeTop / capturedH) * 100).toFixed(1)}%`
    );
  }

  const formatVideoTime = useCallback((secs: number): string => {
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  }, []);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  }, []);

  const handleSeek = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const t = parseFloat(e.target.value);
    if (videoRef.current) videoRef.current.currentTime = t;
    setCurrentTime(t);
  }, []);

  const validateVideoFormat = (blob: Blob): boolean => {
    if (!blob.type.includes("mp4") && !blob.type.includes("webm")) {
      console.warn("⚠️ Video blob is not MP4/WebM format:", blob.type);
      return false;
    }
    if (blob.size === 0) {
      console.warn("⚠️ Video blob is empty");
      return false;
    }
    console.log("✅ Video format validation passed:", {
      type: blob.type,
      size: blob.size,
    });
    return true;
  };

  const handleVideoError = (
    error: React.SyntheticEvent<HTMLVideoElement, Event>,
  ) => {
    console.error("❌ Video playback error:", error);
    const videoElement = error.currentTarget;
    console.error("Video error details:", {
      error: videoElement.error,
      networkState: videoElement.networkState,
      readyState: videoElement.readyState,
      src: videoElement.src?.substring(0, 50),
    });

    setBlobError(
      "Failed to load video. The video source may be invalid or corrupted.",
    );
  };

  useEffect(() => {
    if (!preview) {
      setEffectiveDataUrl("");
      setIsBlobLoading(false);
      setBlobError("");
      return;
    }

    console.log("📹 ScreenRecordingPreview - Processing preview:", {
      hasBlob: !!preview.blob,
      blobSize: preview.blob?.size || 0,
      blobType: preview.blob?.type,
      dataUrl: preview.dataUrl?.substring(0, 50),
      isBlobUrl: preview.dataUrl?.startsWith("blob:"),
      isHttpUrl: preview.dataUrl?.startsWith("http"),
    });

    if (preview.blob && preview.blob.size === 0) {
      console.log("⏳ Blob is empty, waiting for it to load...");
      setIsBlobLoading(true);
      setEffectiveDataUrl("");
      setBlobError("");
      return;
    }

    if (preview.blob && preview.blob.size > 0) {
      console.log("✅ Creating fresh blob URL for preview");

      if (!validateVideoFormat(preview.blob)) {
        console.warn("⚠️ Invalid video format, but will try to play anyway");
      }

      if (effectiveDataUrl && effectiveDataUrl.startsWith("blob:")) {
        try {
          URL.revokeObjectURL(effectiveDataUrl);
          console.log("🧹 Revoked old blob URL");
        } catch (e) {
          console.warn("Could not revoke old URL:", e);
        }
      }

      const freshUrl = URL.createObjectURL(preview.blob);
      console.log("✅ Fresh blob URL created:", freshUrl.substring(0, 50));

      setEffectiveDataUrl(freshUrl);
      setIsBlobLoading(false);
      setBlobError("");
    } else if (preview.dataUrl && !preview.dataUrl.startsWith("blob:")) {
      console.log(
        "✅ Using remote URL directly:",
        preview.dataUrl.substring(0, 50),
      );
      setEffectiveDataUrl(preview.dataUrl);
      setIsBlobLoading(false);
      setBlobError("");
    } else {
      console.error("❌ No valid video source available");
      setBlobError("No valid video source available");
      setIsBlobLoading(false);
      setEffectiveDataUrl("");
    }

    return () => {
      if (effectiveDataUrl && effectiveDataUrl.startsWith("blob:")) {
        console.log("🧹 Cleanup: Revoking blob URL on unmount/change");
        URL.revokeObjectURL(effectiveDataUrl);
      }
    };
  }, [preview?.blob?.size, preview?.dataUrl, preview?.blob?.type]);

  useEffect(() => {
    if (preview?.blob && preview.blob.size > 0 && isBlobLoading) {
      console.log("✅ Blob finished loading:", {
        size: preview.blob.size,
        type: preview.blob.type,
      });
      setIsBlobLoading(false);
    }
  }, [preview?.blob?.size, preview?.blob?.type, isBlobLoading]);

  useEffect(() => {
    if (preview) {
      console.log("📹 ScreenRecordingPreview - New preview loaded:", {
        hasBlobData: !!preview.blob,
        blobSize: preview.blob?.size || 0,
        hasDataUrl: !!preview.dataUrl,
      });

      if (!isEditMode) {
        setFeedback("");
        setHasInteractedWithEditor(false);
        setFeedbackError("");
      }

      setAnimationPhase("opening");
      setIsAnimating(true);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimationPhase("open");
          setTimeout(() => {
            setIsAnimating(false);
          }, 500);
        });
      });
    } else {
      setAnimationPhase("closed");
      setIsAnimating(false);
    }
  }, [preview?.timestamp, isEditMode]);

  useEffect(() => {
    if (isEditMode && !isRecordingInitialized) {
      console.log("📹 ScreenRecordingPreview - Initializing edit mode:", {
        existingFeedback: existingFeedback?.substring(0, 50),
        hasExistingRecording: !!existingDataUrl || !!existingRecordingBlob,
      });

      if (existingFeedback) {
        setFeedback(existingFeedback);
        setHasInteractedWithEditor(true);
      }

      setIsRecordingInitialized(true);
      console.log("✅ Edit mode initialized for recording");
    }
  }, [isEditMode, existingFeedback, existingDataUrl, isRecordingInitialized]);

  // Start cropping in background as soon as video dimensions are known
  useEffect(() => {
    if (croppingPromiseRef.current !== null) return;
    if (!iframeDimensions || !videoNatural || videoNatural.w <= 0 || !preview?.blob) return;

    const scaleX = videoNatural.w / capturedW;
    const scaleY = videoNatural.h / capturedH;
    const cropSx = Math.round(effectiveIframeLeft * scaleX);
    const cropSy = Math.round(effectiveIframeTop * scaleY);
    const cropSw = Math.min(Math.round(iframeW_crop * scaleX), videoNatural.w - cropSx);
    const cropSh = Math.min(Math.round(iframeH_crop * scaleY), videoNatural.h - cropSy);

    if (cropSw <= 0 || cropSh <= 0) return;

    console.log("✂️ [Background] Starting video crop:", { cropSx, cropSy, cropSw, cropSh });
    croppingPromiseRef.current = cropVideoBlob(preview.blob, cropSx, cropSy, cropSw, cropSh)
      .then((blob) => { console.log("✅ [Background] Crop complete:", { size: blob.size, type: blob.type }); return blob; })
      .catch((err) => { console.warn("⚠️ [Background] Crop failed:", err); return null; });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoNatural?.w, videoNatural?.h]);

  const handleDiscard = () => {
    if (isAnimating) return;

    console.log("📹 ScreenRecordingPreview - Starting closing animation");
    setIsAnimating(true);
    setAnimationPhase("closing");

    setTimeout(() => {
      onClose();
      setAnimationPhase("closed");
      setIsAnimating(false);
    }, 300);
  };

  const handleSubmit = async () => {
    // Validate that feedback has actual text content
    if (!feedback.trim() || !hasActualTextContent(feedback)) {
      console.warn(
        "📹 ScreenRecordingPreview - Cannot submit: No feedback content",
      );
      setFeedbackError("Please add feedback before submitting the recording.");
      console.log(
        "🔴 Error message set:",
        "Please add feedback before submitting the recording.",
      );
      return;
    }

    // Clear error if validation passed
    setFeedbackError("");

    console.log("📹 Pre-submit blob validation:", {
      hasPreview: !!preview,
      hasBlob: !!preview?.blob,
      blobSize: preview?.blob?.size || 0,
      blobType: preview?.blob?.type,
      isBlobLoading,
      hasValidVideoSource,
      effectiveDataUrl: effectiveDataUrl?.substring(0, 50),
    });

    if (isBlobLoading) {
      console.warn(
        "📹 ScreenRecordingPreview - Cannot submit: Video is still loading",
      );
      alert("Video is still loading. Please wait a moment and try again.");
      return;
    }

    if (!preview || !preview.blob) {
      console.error("❌ Cannot submit: No preview or blob available");
      alert("Recording data is missing. Please try recording again.");
      return;
    }

    if (preview.blob.size === 0) {
      console.error("❌ Cannot submit: Blob is empty", {
        blobType: preview.blob.type,
        timestamp: preview.timestamp,
      });
      alert("Recording data is empty. Please try recording again.");
      return;
    }

    const validTypes = ["video/mp4", "video/webm", "video/quicktime"];
    if (
      !validTypes.some((type) => preview.blob.type.includes(type.split("/")[1]))
    ) {
      console.warn("⚠️ Unusual video type:", preview.blob.type);
    }

    if (!hasValidVideoSource) {
      console.warn(
        "📹 ScreenRecordingPreview - Cannot submit: No valid video source",
      );
      alert("No valid video available. Please try recording again.");
      return;
    }

    if (onSubmit) {
      console.log("📹 ScreenRecordingPreview - Creating submission:", {
        feedbackLength: feedback.trim().length,
        hasAudio: true,
        duration: preview.duration,
        blobType: preview.blob.type,
        blobSize: preview.blob.size,
        blobSizeInMB: (preview.blob.size / (1024 * 1024)).toFixed(2),
        uniqueFeedbackId: uniqueFeedbackId,
        effectiveDataUrl: effectiveDataUrl.substring(0, 50),
        isEditMode,
        isFromViewListFeedback,
      });

      let finalBlob = preview.blob;

      // Validate blob one more time
      if (finalBlob.size === 0) {
        console.error("❌ Final blob validation failed - blob is empty");
        alert("Recording data is invalid. Please try recording again.");
        return;
      }

      // Convert to MP4 if needed
      if (
        !preview.blob.type.includes("mp4") &&
        !preview.blob.type.includes("webm")
      ) {
        console.log("🔄 Converting blob to MP4 format for submission");
        finalBlob = new Blob([preview.blob], { type: "video/mp4" });
      }

      // Final validation
      if (finalBlob.size === 0) {
        console.error("❌ Final blob is empty after conversion");
        alert("Recording data conversion failed. Please try recording again.");
        return;
      }

      // Use the background-cropped video if available (crop started when preview opened)
      if (iframeDimensions && croppingPromiseRef.current) {
        setIsSubmitting(true);
        const cropped = await croppingPromiseRef.current;
        if (cropped) {
          console.log("✂️ Using background-cropped video:", { size: cropped.size, type: cropped.type });
          finalBlob = cropped;
        }
      }

      // ✅ NEW: Check if editing from ViewListFeedback - use reshare API
      if (isEditMode && uniqueFeedbackId && isFromViewListFeedback) {
        console.log(
          "✏️ Edit mode from ViewListFeedback - using UPLOAD_RESHARE_FEEDBACK API:",
          uniqueFeedbackId,
        );

        // 🔥 SET isSubmitting to true BEFORE async operation
        setIsSubmitting(true);

        // Use async IIFE to handle reshare API call
        (async () => {
          try {
            console.log("📹 Recording blob prepared for reshare:", {
              size: finalBlob.size,
              sizeInMB: (finalBlob.size / (1024 * 1024)).toFixed(2),
              type: finalBlob.type,
            });

            // Call reshare API with recording as screenshot_url
            // (API expects screenshot_url field for media files)
            const reshareResult = await ReshareApiService.reshareFeedback(
              uniqueFeedbackId,
              feedback, // HTML formatted message
              finalBlob, // Send recording as screenshot_url
              undefined, // No voice for recordings
            );

            console.log(
              "✅ Reshare API completed successfully:",
              reshareResult,
            );

            // Create submission to update local state
            const startRect =
              previewContainerRef.current?.getBoundingClientRect() || null;

            const submission: RecordingSubmission = {
              id: `recording-${Date.now()}-${Math.random()
                .toString(36)
                .substr(2, 9)}`,
              recordingBlob: finalBlob,
              dataUrl: effectiveDataUrl,
              feedback: feedback,
              hasAudio: true,
              duration: preview.duration,
              timestamp: preview.timestamp,
              uniqueFeedbackId: uniqueFeedbackId,
              startRect: startRect,
            };

            console.log("📹 Passing updated submission to parent component");
            onSubmit(submission);

            // Close with animation
            setAnimationPhase("closing");
            setIsAnimating(true);

            setTimeout(() => {
              setFeedback("");
              setIsSubmitting(false); // Reset submitting state
              onClose();
              setAnimationPhase("closed");
              setIsAnimating(false);
            }, 300);
          } catch (reshareError) {
            console.error("❌ Reshare API failed:", reshareError);
            setIsSubmitting(false); // Reset on error
            const errorMsg =
              reshareError instanceof Error
                ? reshareError.message
                : "Unknown error occurred";
            alert(`Failed to update recording: ${errorMsg}. Please try again.`);
          }
        })();

        return; // Exit early for reshare flow
      }

      // ✅ EXISTING: Regular submission flow (not from ViewListFeedback)
      // 🔥 SET isSubmitting to true for regular flow
      setIsSubmitting(true);

      const startRect =
        previewContainerRef.current?.getBoundingClientRect() || null;
      console.log("📹 Captured start position for genie effect:", startRect);

      const submission: RecordingSubmission = {
        id: `recording-${Date.now()}-${Math.random()
          .toString(36)
          .substr(2, 9)}`,
        recordingBlob: finalBlob,
        dataUrl: effectiveDataUrl,
        feedback: feedback,
        hasAudio: true,
        duration: preview.duration,
        timestamp: preview.timestamp,
        uniqueFeedbackId: uniqueFeedbackId,
        startRect: startRect,
      };

      console.log("📹 ScreenRecordingPreview - Submitting recording:", {
        submissionId: submission.id,
        uniqueFeedbackId,
        blobType: submission.recordingBlob?.type,
        blobSize: submission.recordingBlob?.size,
        blobSizeInMB: (submission.recordingBlob?.size || 0) / (1024 * 1024),
        hasStartRect: !!submission.startRect,
        dataUrl: submission.dataUrl.substring(0, 50),
      });

      // Final validation before submission
      if (!submission.recordingBlob || submission.recordingBlob.size === 0) {
        console.error("❌ CRITICAL: Final submission validation failed!");
        setIsSubmitting(false); // Reset on error
        alert("Critical error: Recording data is invalid. Please try again.");
        return;
      }

      onSubmit(submission);

      setAnimationPhase("closing");
      setIsAnimating(true);

      setTimeout(() => {
        setFeedback("");
        setIsSubmitting(false); // Reset submitting state
        onClose();
        setAnimationPhase("closed");
        setIsAnimating(false);
      }, 300);
    }
  };

  const getResponsiveClasses = () => {
    const screenWidth = window.innerWidth;
    let paddingClasses = "p-1";

    // In crop mode the modal height is driven by the crop's aspect ratio,
    // so we only constrain the max-width. max-h would clip the auto-sized video area.
    let modalClasses = "max-w-[75vw] w-full";

    if (iframeDimensions) {
      // Crop mode: width-only constraint; height auto-sizes to the iframe crop ratio.
      if (screenWidth >= 2561) modalClasses = "max-w-[75vw] w-full";
      else if (screenWidth >= 1921) modalClasses = "max-w-[65vw] w-full";
      else if (screenWidth >= 1601) modalClasses = "max-w-[80vw] w-full";
      else if (screenWidth >= 1441) modalClasses = "max-w-[75vw] w-full";
      else modalClasses = "max-w-[75vw] w-full";
    } else {
      // Non-crop mode: keep existing width + height constraints.
      if (screenWidth >= 2561) modalClasses = "max-w-[75vw] w-full max-h-[65vh]";
      else if (screenWidth >= 1921) modalClasses = "max-w-[65vw] w-full max-h-[79vh]";
      else if (screenWidth >= 1601) modalClasses = "max-w-[80vw] w-full max-h-[78vh]";
      else if (screenWidth >= 1441) modalClasses = "max-w-[75vw] w-full max-h-[78vh]";
      else modalClasses = "max-w-[75vw] w-full max-h-[65vh]";
    }

    return {
      modalClasses,
      paddingClasses,
    };
  };

  const responsiveClasses = getResponsiveClasses();

  console.log("📹 ScreenRecordingPreview - Rendering with:", {
    uniqueFeedbackId,
    isValidFeedbackId:
      uniqueFeedbackId && String(uniqueFeedbackId).trim() !== "",
    hasOnSubmit: !!onSubmit,
    hasContent,
    feedbackLength: feedback.length,
    blobType: preview?.blob?.type,
    blobSize: preview?.blob?.size,
    isBlobLoading,
    hasValidVideoSource,
    effectiveDataUrl: effectiveDataUrl.substring(0, 50),
    blobError,
    feedbackError,
    shouldEnableButton,
  });

  return createPortal(
    <>
      <style>{`
        @keyframes zoomIn {
          from {
            opacity: 0;
            transform: scale(0.1);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes zoomOut {
          from {
            opacity: 1;
            transform: scale(1);
          }
          to {
            opacity: 0;
            transform: scale(0.1);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes fadeOut {
          from {
            opacity: 1;
          }
          to {
            opacity: 0;
          }
        }

        .zoom-overlay-enter {
          animation: fadeIn 0.5s ease-out forwards;
        }

        .zoom-overlay-exit {
          animation: fadeOut 0.3s ease-out forwards;
        }

        .zoom-content-enter {
          animation: zoomIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }

        .zoom-content-exit {
          animation: zoomOut 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .loading-spinner {
          animation: spin 1s linear infinite;
        }

        .animate-fadeIn {
          animation: fadeIn 0.2s ease-in;
        }

        .video-seek-range {
          -webkit-appearance: none;
          appearance: none;
          width: 100%;
          height: 3px;
          background: transparent;
          cursor: pointer;
          outline: none;
          border: none;
        }
        .video-seek-range::-webkit-slider-runnable-track {
          height: 3px;
          border-radius: 2px;
          background: linear-gradient(
            to right,
            #4F8EF7 0%,
            #4F8EF7 var(--seek-pct, 0%),
            #D1D5DB var(--seek-pct, 0%),
            #D1D5DB 100%
          );
        }
        .video-seek-range::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: #4F8EF7;
          margin-top: -4.5px;
          cursor: pointer;
          box-shadow: 0 0 0 2px #fff, 0 0 0 3px #4F8EF740;
          transition: box-shadow 0.15s;
        }
        .video-seek-range::-webkit-slider-thumb:hover {
          box-shadow: 0 0 0 2px #fff, 0 0 0 4px #4F8EF760;
        }
        .video-seek-range::-moz-range-track {
          height: 3px;
          border-radius: 2px;
          background: #D1D5DB;
        }
        .video-seek-range::-moz-range-progress {
          height: 3px;
          border-radius: 2px;
          background: #4F8EF7;
        }
        .video-seek-range::-moz-range-thumb {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: #4F8EF7;
          border: none;
          cursor: pointer;
        }
      `}</style>

      <div
        className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-2 ${
          animationPhase === "opening" ? "zoom-overlay-enter" : ""
        } ${animationPhase === "closing" ? "zoom-overlay-exit" : ""}`}
      >
        <div
          ref={previewContainerRef}
          className={`bg-white rounded-[8px] ${
            responsiveClasses.modalClasses
          } overflow-hidden flex flex-col ${
            animationPhase === "opening" ? "zoom-content-enter" : ""
          } ${animationPhase === "closing" ? "zoom-content-exit" : ""}`}
        >
          <div
            className={`flex flex-1 ${responsiveClasses.paddingClasses} overflow-hidden`}
          >
            {/* Main Video Area */}
            <div className={`flex-1 flex flex-col overflow-hidden rounded-[4px] ${
              hasValidVideoSource && !isBlobLoading && !blobError
                ? "bg-white"
                : "bg-gray-100 items-center justify-center"
            }`}>
              {isBlobLoading ? (
                <div className="flex flex-col items-center justify-center space-y-4">
                  <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full loading-spinner"></div>
                  <p
                    className="text-gray-600 text-lg font-medium"
                    style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                  >
                    Loading video...
                  </p>
                </div>
              ) : blobError ? (
                <div className="flex flex-col items-center justify-center space-y-4 p-8">
                  <svg
                    className="w-16 h-16 text-red-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <p
                    className="text-gray-600 text-lg font-medium text-center"
                    style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                  >
                    {blobError}
                  </p>
                  <Button
                    variant="primary"
                    size="md"
                    onClick={handleDiscard}
                    className="mt-4"
                    style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                  >
                    Close
                  </Button>
                </div>
              ) : hasValidVideoSource ? (
                iframeDimensions ? (
                  <>
                    <div
                      style={{
                        position: "relative",
                        width: "100%",
                        flexShrink: 0,
                        aspectRatio: `${iframeW_crop} / ${iframeH_crop}`,
                        overflow: "hidden",
                        borderRadius: "4px",
                        background: "#000",
                        WebkitMaskImage: "linear-gradient(#000, #000)",
                        maskImage: "linear-gradient(#000, #000)",
                      }}
                    >
                      <video
                        ref={videoRef}
                        src={effectiveDataUrl}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: `${(capturedW / iframeW_crop) * 100}%`,
                          maxWidth: "none",
                          transform: `translate(${-(effectiveIframeLeft / capturedW) * 100}%, ${-(effectiveIframeTop / capturedH) * 100}%)`,
                        }}
                        autoPlay={false}
                        preload="metadata"
                        onError={handleVideoError}
                        onLoadedMetadata={(e) => {
                          setVideoDuration(e.currentTarget.duration);
                          const vw = e.currentTarget.videoWidth;
                          const vh = e.currentTarget.videoHeight;
                          if (vw > 0 && vh > 0) setVideoNatural({ w: vw, h: vh });
                        }}
                        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
                        onPlay={() => setIsPlaying(true)}
                        onPause={() => setIsPlaying(false)}
                        onEnded={() => setIsPlaying(false)}
                        onLoadedData={(e) => {
                          const vw = e.currentTarget.videoWidth;
                          const vh = e.currentTarget.videoHeight;
                          if (vw > 0 && vh > 0 && (!videoNatural || videoNatural.w === 0)) {
                            setVideoNatural({ w: vw, h: vh });
                          }
                        }}
                      />
                    </div>
                    {/* Controls */}
                    <div className="flex-shrink-0 flex justify-center -mx-[8px] pt-2 bg-white">
                    <div className="flex items-center gap-2 px-3 py-2 rounded-[4px]" style={{ background: "#F9F9F9", border: "0.5px solid #B7BBCA", width: "calc(100% - 16px)" }}>
                      <button
                        onClick={togglePlay}
                        className="text-gray-700 flex-shrink-0 hover:text-blue-500 transition-colors"
                        title={isPlaying ? "Pause" : "Play"}
                      >
                        {isPlaying ? (
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <rect x="6" y="4" width="4" height="16" rx="1" />
                            <rect x="14" y="4" width="4" height="16" rx="1" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        )}
                      </button>
                      <span
                        className="text-gray-700 text-xs flex-shrink-0 tabular-nums"
                        style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                      >
                        {formatVideoTime(currentTime)} / {formatVideoTime(videoDuration)}
                      </span>
                      <input
                        type="range"
                        min={0}
                        max={videoDuration || 0}
                        step={0.1}
                        value={currentTime}
                        onChange={handleSeek}
                        className="video-seek-range flex-1"
                        style={{
                          ["--seek-pct" as any]: `${videoDuration ? (currentTime / videoDuration) * 100 : 0}%`,
                        }}
                      />
                    </div>
                    </div>
                  </>
                ) : (
                  // Fallback: no crop — mirrors iframeDimensions branch structure
                  <>
                    <div
                      style={{
                        position: "relative",
                        width: "100%",
                        flexShrink: 0,
                        aspectRatio: videoNatural ? `${videoNatural.w} / ${videoNatural.h}` : "16 / 9",
                        maxHeight: "calc(65vh - 80px)",
                        overflow: "hidden",
                        borderRadius: "4px",
                        background: "#000",
                      }}
                    >
                      <video
                        ref={videoRef}
                        src={effectiveDataUrl}
                        style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
                        autoPlay={false}
                        preload="metadata"
                        onError={handleVideoError}
                        onLoadedMetadata={(e) => {
                          setVideoDuration(e.currentTarget.duration);
                          const vw = e.currentTarget.videoWidth;
                          const vh = e.currentTarget.videoHeight;
                          if (vw > 0 && vh > 0) setVideoNatural({ w: vw, h: vh });
                        }}
                        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
                        onPlay={() => setIsPlaying(true)}
                        onPause={() => setIsPlaying(false)}
                        onEnded={() => setIsPlaying(false)}
                        onLoadedData={(e) => {
                          const vw = e.currentTarget.videoWidth;
                          const vh = e.currentTarget.videoHeight;
                          if (vw > 0 && vh > 0 && (!videoNatural || videoNatural.w === 0)) {
                            setVideoNatural({ w: vw, h: vh });
                          }
                        }}
                      />
                    </div>
                    {/* Controls — identical to iframeDimensions branch */}
                    <div className="flex-shrink-0 flex justify-center -mx-[8px] pt-2 bg-white">
                      <div className="flex items-center gap-2 px-3 py-2 rounded-[4px]" style={{ background: "#F9F9F9", border: "0.5px solid #B7BBCA", width: "calc(100% - 16px)" }}>
                        <button
                          onClick={togglePlay}
                          className="text-gray-700 flex-shrink-0 hover:text-blue-500 transition-colors"
                          title={isPlaying ? "Pause" : "Play"}
                        >
                          {isPlaying ? (
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                              <rect x="6" y="4" width="4" height="16" rx="1" />
                              <rect x="14" y="4" width="4" height="16" rx="1" />
                            </svg>
                          ) : (
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          )}
                        </button>
                        <span
                          className="text-gray-700 text-xs flex-shrink-0 tabular-nums"
                          style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                        >
                          {formatVideoTime(currentTime)} / {formatVideoTime(videoDuration)}
                        </span>
                        <input
                          type="range"
                          min={0}
                          max={videoDuration || 0}
                          step={0.1}
                          value={currentTime}
                          onChange={handleSeek}
                          className="video-seek-range flex-1"
                          style={{
                            ["--seek-pct" as any]: `${videoDuration ? (currentTime / videoDuration) * 100 : 0}%`,
                          }}
                        />
                      </div>
                    </div>
                  </>
                )
              ) : (
                <div className="flex flex-col items-center justify-center space-y-4">
                  <p
                    className="text-gray-600 text-lg font-medium"
                    style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                  >
                    No video available
                  </p>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="w-[22%] min-w-[280px] max-w-[440px] bg-white flex flex-col flex-shrink-0">
              {/* TextEditor Component */}
              <div className="pl-4 flex-1 overflow-hidden">
                <TextEditor
                  value={feedback}
                  onChange={(value) => {
                    setFeedback(value);
                    // Clear error when user starts typing actual content
                    if (feedbackError) {
                      // Check if there's actual text content (not just HTML tags)
                      const textOnly = value.replace(/<[^>]*>/g, "").trim();
                      if (textOnly.length > 0) {
                        setFeedbackError("");
                      }
                    }
                  }}
                  disabled={isAnimating || isBlobLoading || !!blobError}
                  placeholder="Type your feedback about this recording..."
                  onFocus={() => {
                    setIsTextEditorFocused(true);
                    setHasInteractedWithEditor(true);
                  }}
                  onBlur={() => {
                    setIsTextEditorFocused(false);
                  }}
                />
              </div>

              {/* Error message display - Fixed position between editor and buttons */}
              <div className="pl-4 pr-1 min-h-[24px]">
                {feedbackError && (
                  <div
                    className="pb-2 text-red-500 text-sm font-medium animate-fadeIn"
                    style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                  >
                    {feedbackError}
                  </div>
                )}
              </div>
              {/* ADD THIS NEW SECTION - Warning message in edit mode */}
              {isEditMode && (
                <div className="flex items-start gap-2 ml-4 mr-1 mb-2 px-3 py-3 bg-[#FFF9F0]  rounded-[4px]">
                  <AlertIcon className="w-4 h-4" />
                  <p
                    className="text-xs 2xl:text-[14px] text-[#1F1E1D] "
                    style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                  >
                    Only the feedback text can be edited.
                  </p>
                </div>
              )}

              {/* Bottom Section - Separator and Buttons */}
              <div className="ml-4 mr-1 border-t border-gray-200" />
              <div className="pl-4 pt-4 pr-1 pb-1 flex space-x-3">
                <Button
                  variant="cancel"
                  size={
                    window.innerWidth >= 2560
                      ? "lg"
                      : window.innerWidth >= 1920
                        ? "md"
                        : "md"
                  }
                  onClick={handleDiscard}
                  className="flex-1"
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                  disabled={isAnimating}
                >
                  Discard
                </Button>

                {onSubmit && (
                  <Button
                    variant={
                      shouldEnableButton && hasValidVideoSource && !blobError
                        ? "primary"
                        : "secondary"
                    }
                    size={
                      window.innerWidth >= 2560
                        ? "lg"
                        : window.innerWidth >= 1920
                          ? "md"
                          : "md"
                    }
                    onClick={handleSubmit}
                    disabled={
                      !shouldEnableButton ||
                      isBlobLoading ||
                      !hasValidVideoSource ||
                      !!blobError
                    }
                    className="flex-1"
                    style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                    title={
                      blobError
                        ? "Video failed to load"
                        : isBlobLoading
                          ? "Loading video..."
                          : !hasValidVideoSource
                            ? "Video is still loading"
                            : !shouldEnableButton
                              ? "Please click in the text editor to add feedback"
                              : !uniqueFeedbackId
                                ? "Warning: No feedback ID available"
                                : preview.blob?.type.includes("mp4")
                                  ? `${
                                      isEditMode ? "Update" : "Submit"
                                    } MP4 recording with feedback`
                                  : `${
                                      isEditMode ? "Update" : "Submit"
                                    } recording with feedback (will be converted to MP4)`
                    }
                  >
                    {isSubmitting ? (
                      <span className="inline-block relative flex-shrink-0" style={{ width: 40, height: 16 }}>
                        <Lottie
                          animationData={DotLoader}
                          loop={true}
                          style={{
                            width: 120,
                            height: 48,
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                          }}
                        />
                      </span>
                    ) : isFromViewListFeedback ? (
                      "Save & notify"
                    ) : isEditMode ? (
                      "Update"
                    ) : (
                      "Add"
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default ScreenRecordingPreview;
